import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastContainer } from './components/common/Toast';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import MeetingPage from './pages/MeetingPage';
import RoomPage from './pages/RoomPage';
import RoomSettingsPage from './pages/RoomSettingsPage';
import ParticipantsPopoutPage from './pages/ParticipantsPopoutPage';
import ScreenSharePopoutPage from './pages/ScreenSharePopoutPage';
import SettingsPage from './pages/SettingsPage';
import PairPage from './pages/PairPage';
import JoinPage from './pages/JoinPage';
import NotFoundPage from './pages/NotFoundPage';

/**
 * Root application component with routing.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRoutes />
        <ToastContainer />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Application routes with authentication gating.
 */
function AppRoutes() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route
        path="/"
        element={
          isAuthenticated ? <HomePage /> : <LandingPage />
        }
      />

      <Route
        path="/meeting/:roomCode"
        element={
          isAuthenticated ? <MeetingPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/room/:roomId"
        element={
          isAuthenticated ? <RoomPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/room/:roomId/settings"
        element={
          isAuthenticated ? <RoomSettingsPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/meeting/:roomCode/participants"
        element={
          isAuthenticated ? <ParticipantsPopoutPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/meeting/:roomCode/screenshare"
        element={
          isAuthenticated ? <ScreenSharePopoutPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/settings"
        element={
          isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/pair"
        element={
          isAuthenticated ? <PairPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/join"
        element={
          isAuthenticated ? <JoinPage /> : <Navigate to="/login" replace />
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Loading screen shown during auth initialization.
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  );
}
